import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import BarcodeGenerationModal from './BarcodeGenerationModal';
import SequentialBarcodeSection from '../components/SequentialBarcodeSection';

// ✅ NEW: Create Items Batch Modal for non-volume mode
const CreateItemsBatchModal = ({
  open,
  onClose,
  onCreateItems,
  holdingLocationId,
  template,
  classificationNumber,
  authorNumber,
  currentItemsCount,
}) => {
  const intl = useIntl();
  const [itemCount, setItemCount] = useState(1);
  const [showAdvancedBarcodeModal, setShowAdvancedBarcodeModal] = useState(false);
  const [barcodeSettings, setBarcodeSettings] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setItemCount(1);
      setBarcodeSettings(null);
    }
  }, [open]);

  // Handle barcode settings change from the reusable component
  const handleBarcodeSettingsChange = (settings) => {
    setBarcodeSettings(settings);
  };

  const handleCreate = () => {
    const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);
    if (validItemCount > 0) {
      onCreateItems(validItemCount, barcodeSettings);
      onClose();
    }
  };

  const handleAdvancedBarcodeConfirm = (settings) => {
    setShowAdvancedBarcodeModal(false);
    onCreateItems(Math.max(1, parseInt(itemCount, 10) || 1), settings);
    onClose();
  };

  const isValidItemCount =
    itemCount &&
    !Number.isNaN(parseInt(itemCount, 10)) &&
    parseInt(itemCount, 10) > 0 &&
    parseInt(itemCount, 10) <= 1000;

  // Enhanced form validation
  const isFormValid = () => {
    const basicValidation = isValidItemCount;

    // If barcode settings exist, check if they are valid
    if (barcodeSettings) {
      return basicValidation && barcodeSettings.isValid;
    }

    return basicValidation;
  };

  // Render footer for fixed footer
  const renderFooter = (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleCreate}
        disabled={!isFormValid()}
        marginBottom0
      >
        <FormattedMessage
          id="ui-plugin-inventory-templates.createItemsBatchModal.create"
          values={{ count: Math.max(1, parseInt(itemCount, 10) || 1) }}
          defaultMessage="Create {count} Items"
        />
      </Button>
      <Button onClick={onClose} marginBottom0>
        <FormattedMessage
          id="ui-plugin-inventory-templates.createItemsBatchModal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    </ModalFooter>
  );

  return (
    <>
      <Modal
        open={open}
        footer={renderFooter}
        label={
          <FormattedMessage
            id="ui-plugin-inventory-templates.createItemsBatchModal.label"
            defaultMessage="Create Multiple Items"
          />
        }
        onClose={onClose}
        size="small"
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createItemsBatchModal.description"
              defaultMessage="Create multiple items for this holdings record:"
            />
          </p>

          {/* Item Count Input */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.itemCount.label"
                  defaultMessage="Number of Items to Create"
                />
              }
              type="number"
              min="1"
              max="1000"
              value={itemCount}
              onChange={(e) => setItemCount(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.createItemsBatchModal.itemCount.placeholder',
                defaultMessage: 'Enter number of items'
              })}
              required
            />
          </div>

          {/* Sequential Barcode Section - Using Reusable Component */}
          {isValidItemCount && (
            <SequentialBarcodeSection
              itemCount={itemCount}
              onBarcodeSettingsChange={handleBarcodeSettingsChange}
              sectionTitle={intl.formatMessage(
                { id: 'ui-plugin-inventory-templates.sequentialBarcode.section.title' },
                { count: Math.max(1, parseInt(itemCount, 10) || 1) }
              )}
              checkboxLabel={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.useSequential.label'
              })}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.placeholder'
              })}
              onAdvancedClick={() => setShowAdvancedBarcodeModal(true)}
            />
          )}

          {/* Preview */}
          {isValidItemCount && (
            <div
              style={{
                backgroundColor: '#e8f5e8',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                border: '1px solid #c3e6c3',
              }}
            >
              <div
                style={{
                  fontWeight: '600',
                  marginBottom: '4px',
                  color: '#2d5a2d',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.preview.label"
                  defaultMessage="Items to be created:"
                />
              </div>
              <div style={{ fontSize: '14px', color: '#2d5a2d' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.preview.details"
                  values={{
                    itemCount: Math.max(1, parseInt(itemCount, 10) || 1),
                    startCopyNumber: currentItemsCount + 1,
                    endCopyNumber:
                      currentItemsCount +
                      Math.max(1, parseInt(itemCount, 10) || 1),
                    classificationNumber: classificationNumber || '',
                    authorNumber: authorNumber || '',
                  }}
                  defaultMessage="{itemCount} items with copy numbers {startCopyNumber} to {endCopyNumber}"
                />
              </div>
            </div>
          )}

          {/* Info */}
          <div
            style={{
              backgroundColor: '#f8f9fa',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #dee2e6',
            }}
          >
            <div
              style={{
                fontWeight: '600',
                marginBottom: '8px',
                color: '#495057',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.createItemsBatchModal.info.title"
                defaultMessage="Each item will have:"
              />
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#6c757d' }}>
              <li>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.info.barcode"
                  defaultMessage="Auto-generated barcode (if enabled)"
                />
              </li>
              <li>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.info.copyNumber"
                  defaultMessage="Sequential copy number"
                />
              </li>
              <li>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.info.location"
                  defaultMessage="Same permanent location as holdings"
                />
              </li>
              <li>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createItemsBatchModal.info.callNumber"
                  values={{
                    callNumber:
                      `${classificationNumber || ''}${authorNumber || ''}` ||
                      'None',
                  }}
                  defaultMessage="Call number: {callNumber}"
                />
              </li>
            </ul>
          </div>
        </div>

      </Modal>

      {/* Advanced Barcode Generation Modal */}
      <BarcodeGenerationModal
        open={showAdvancedBarcodeModal}
        onClose={() => setShowAdvancedBarcodeModal(false)}
        onConfirm={handleAdvancedBarcodeConfirm}
        itemCount={Math.max(1, parseInt(itemCount, 10) || 1)}
        enableAutoGenerator={false}
        autoGenerate={false}
        generateBarcodeValue={() => ''}
        locationId={holdingLocationId}
        locationCode=""
        existingBarcodes={[]}
        title={
          <FormattedMessage
            id="ui-plugin-inventory-templates.createItemsBatchModal.advancedBarcodeModal.title"
            defaultMessage="Advanced Barcode Generation Settings"
          />
        }
      />
    </>
  );
};

CreateItemsBatchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateItems: PropTypes.func.isRequired,
  holdingLocationId: PropTypes.string,
  template: PropTypes.object,
  classificationNumber: PropTypes.string,
  authorNumber: PropTypes.string,
  currentItemsCount: PropTypes.number,
};

export default CreateItemsBatchModal;
